import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, HardHat, Layers, ChefHat } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Cubeworx Builders & Development — concept site. Grounded, workmanlike
  residential/commercial builder identity in concrete-gray + safety orange,
  built entirely on real jobsite and completed-building photography.
*/

const ORANGE = "#E85D2A";
const CONCRETE = "#2C2C2C";
const CREAM = "#F6F5F2";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/cubeworxbuilders/";
const NAME = "Cubeworx Builders & Development";
const SLUG = "cubeworx-builders-concept";
const P = "/construction/cubeworx";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function CubeworxConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Our Builds", "#build"], ["Project Gallery", "#work"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'DM Sans', sans-serif" }} data-testid="cubeworx-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Sora:wght@500;600;700&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Sora', sans-serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.25)]" : ""}`} style={{ background: CONCRETE }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="cx-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded object-cover" />
            <span className="text-lg text-white font-extrabold">CUBEWORX</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold text-white/75 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="cx-header-cta" className="hidden sm:inline-flex px-5 py-2.5 text-sm font-bold text-white" style={{ background: ORANGE }}>
            Get a Free Quote
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="cx-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: CONCRETE }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="cx-mobile-cta" className="block mt-2 text-center px-5 py-3 text-sm font-bold text-white" style={{ background: ORANGE }}>Get a Free Quote</a>
          </div>
        )}
      </header>

      {/* HERO — centered glass card composition (distinct from photo-bottom-overlay style) */}
      <section className="relative min-h-[80vh] flex items-center justify-center" data-testid="cx-hero">
        <img src={`${P}/louvered-building.jpg`} alt="A building completed by Cubeworx Builders" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `${CONCRETE}66` }} />
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 w-full text-center">
          <motion.div {...fadeUp} className="rounded-2xl p-8 sm:p-12" style={{ background: "rgba(20,20,20,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] drop-shadow" style={{ color: "#FFC79A" }}>Dumaguete City, Negros Oriental</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white font-extrabold">Solid Builds, Straight Talk.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed">
              Residential and commercial building construction — from concrete-block homes to finished commercial structures across Dumaguete City.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a href="#contact" data-testid="cx-hero-cta" className="px-7 py-3.5 text-sm font-bold text-white" style={{ background: ORANGE }}>Get a Free Quote</a>
              <a href="#work" data-testid="cx-hero-work" className="border border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">View Our Builds</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section id="build" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: ORANGE }}>What We Build</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-snug">Homes &amp; Commercial Buildings</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [HardHat, "House Construction", "CHB and structural home builds, from foundation through roofing."],
            [Layers, "Commercial Structures", "Commercial building shells and finishing for local businesses."],
            [ChefHat, "Interior Fit-Out", "Interior finishing including kitchen and cabinetry installation."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: ORANGE }} />
              <h3 className="mt-4 text-lg font-bold">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OUR WORK */}
      <section id="work" className="py-20 md:py-28" style={{ background: CONCRETE }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: ORANGE }}>Real Projects</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white font-extrabold leading-snug">Our Work</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { img: `${P}/completed-building.jpg`, label: "Completed building shell" },
              { img: `${P}/house-framing.jpg`, label: "House under construction" },
              { img: `${P}/kitchen-fitout.jpg`, label: "Interior kitchen fit-out" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="cx-work-item" className="overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-64 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70">{it.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: ORANGE }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-extrabold leading-snug">Get a Quote</h2>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: `${INK}90` }}>Online estimates and appointment-based onsite service are available for prospective projects.</p>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: ORANGE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Dumaguete City, Negros Oriental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: ORANGE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+639352775827" className="hover:underline">0935 277 5827</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: ORANGE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="cx-facebook-link" className="hover:underline">facebook.com/cubeworxbuilders</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="cx"
              accent={ORANGE}
              accentText="#FFFFFF"
              heading="Get a Quote"
              subheading="New house, commercial build or interior fit-out — tell us about the project and we'll follow up."
              projectTypes={["New House Build", "Commercial Building", "Interior Fit-Out", "Other"]}
              phone="+639352775827"
              phoneDisplay="0935 277 5827"
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: CONCRETE, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded object-cover" />
              <span className="text-white text-lg font-extrabold">CUBEWORX BUILDERS</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Residential and commercial building construction in Dumaguete City.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Dumaguete City, Negros Oriental</p>
            <p className="text-sm mt-2">0935 277 5827</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-bold text-white shadow-xl" style={{ background: ORANGE }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
